import type { SessionState } from '@/voice/types'

/**
 * The Blossom: the Namu mark, alive.
 *
 * The brand mark is an open arc of about 270 degrees with a Sahel-orange dot
 * resting at its lower terminus. Nothing here invents a new shape. The arc and
 * the dot are the whole vocabulary, and every session state is expressed by
 * what those two elements do:
 *
 *   idle          a still ring, breathing very slowly. The logo, at rest.
 *   connecting    the arc draws itself in, the dot leading the stroke.
 *   listening     the dot waits at the open gap and sways; the ring glows
 *                 outward with the person's voice. Light travels out.
 *   user_speaking the ring itself deforms with the voice, and the dot is
 *                 pushed outward. The person can see themselves in it.
 *   processing    the arc reaches into its own gap, the dot riding its
 *                 terminus, while the field gathers toward the centre. A
 *                 closing, not a turning. See THINKING_PERIOD_S.
 *   model_speaking the ring fills with light that pulses inward, and the dot
 *                 returns to rest. The inverse of listening.
 *   interrupted   the fill snaps out. It should look like being cut off.
 *   reconnecting  a dashed arc turning slowly. Visibly not idle.
 *   error         the arc drops to Kola and stops. Banked embers, gone out.
 *
 * Written as a plain class with an explicit draw(now) rather than as a React
 * component, so the animation loop never touches React state. Sixty state
 * updates a second would re-render the whole screen for a shape that owns its
 * own pixels.
 */

export interface BlossomFrame {
  state: SessionState
  /** 0..1 loudness of whoever currently has the floor. */
  level: number
  muted: boolean
  held: boolean
  /** Renders a still shape with colour-only state changes. */
  reducedMotion: boolean
}

/**
 * The orb's palette.
 *
 * The arc used to be a hardcoded Harmattan, which is correct on Ink and
 * invisible on cream. It now follows the colour scheme like everything else.
 */
interface Palette {
  /** The stroke. The only colour that differs between the two schemes. */
  arc: readonly [number, number, number]
  /** Multiplier on the dot's halo. Cream needs more than Ink. */
  glowStrength: number
}

/**
 * The brand colours the field is built from. They are the same on both
 * grounds; only the arc follows the scheme, and it is read from the element's
 * resolved `color` rather than from the custom property directly.
 * `getPropertyValue` hands back the *authored* text, so a token defined as
 * `var(--text)` arrives as the literal string "var(--text)" and a hex arrives
 * as "#1c1410". Both defeat any parser. Going through a real inherited
 * property makes the browser do the resolving.
 */
const SAHEL = [232, 147, 90] as const
const KOLA = [107, 62, 30] as const
const CLAY = [237, 217, 176] as const
const HARMATTAN = [247, 240, 227] as const
const PAPER = [255, 250, 241] as const

/**
 * How the field behaves in each state.
 *
 * This table is the entire status indicator now. The words under the orb are
 * gone, so a person has to be able to tell listening from thinking from
 * speaking by watching, which means the numbers here have to be far enough
 * apart to read at a glance and not merely different.
 *
 *   energy      how much colour is in the field at rest
 *   levelGain   how much the voice adds to that
 *   speed       how fast the masses drift
 *   levelSpeed  how much the voice adds to that
 */
interface FlowBehaviour {
  energy: number
  levelGain: number
  speed: number
  levelSpeed: number
}

interface Mass {
  colour: readonly [number, number, number]
  alpha: number
  /** Radius of the gradient, as a multiple of the mark's radius. */
  size: number
  /** How far the centre travels, as a multiple of the mark's radius. */
  ax: number
  ay: number
  /** Angular speeds. Deliberately incommensurable so the drift never repeats. */
  wx: number
  wy: number
  phase: number
}

/**
 * The pale masses that drift over the base gradient.
 *
 * Three is enough, and they are far weaker than they look like they should be.
 * At any real strength, pale masses over the middle of the disc erase exactly
 * the saturation the base gradient exists to provide, and the whole thing goes
 * beige. They ride wide, near the rim, and only suggest movement.
 *
 * Their speeds share no common factor with each other or with the axis
 * rotation, so the field never lands in the same arrangement twice, which is
 * the difference between something alive and a loop.
 */
const WISPS: readonly Mass[] = [
  { colour: PAPER, alpha: 0.2, size: 0.4, ax: 0.42, ay: 0.34, wx: 0.21, wy: 0.17, phase: 0 },
  { colour: PAPER, alpha: 0.15, size: 0.3, ax: 0.38, ay: 0.44, wx: -0.15, wy: 0.19, phase: 1.7 },
  { colour: PAPER, alpha: 0.12, size: 0.24, ax: 0.46, ay: 0.38, wx: 0.29, wy: -0.23, phase: 3.1 },
]

const FLOW_BEHAVIOUR: Record<SessionState, FlowBehaviour> = {
  // At rest: barely tinted, drifting slowly. Present, not waiting on you.
  idle: { energy: 0.88, levelGain: 0, speed: 0.5, levelSpeed: 0 },
  requesting_permission: { energy: 0.88, levelGain: 0, speed: 0.5, levelSpeed: 0 },
  // Connecting turns without brightening: something is happening, not yet you.
  connecting: { energy: 0.92, levelGain: 0, speed: 1.5, levelSpeed: 0 },
  // Listening is the one state driven almost entirely by the voice, so the
  // field is visibly answering the person rather than performing.
  listening: { energy: 0.9, levelGain: 0.1, speed: 0.85, levelSpeed: 1.1 },
  user_speaking: { energy: 0.94, levelGain: 0.06, speed: 1, levelSpeed: 1.6 },
  /*
   * Thinking: slower and cooler than listening, with no voice term.
   *
   * It used to be the fastest thing on screen, at 2.6, which was right when a
   * turn was answered in about a third of a second and is wrong now. Namu's own
   * model takes one to three seconds, and a field whipping round for three
   * seconds reads as agitation. Held that long, the state has to look like
   * somebody considering an answer rather than somebody hurrying.
   */
  processing: { energy: 0.84, levelGain: 0, speed: 0.7, levelSpeed: 0 },
  // Speaking is the brightest the field ever gets, and it pulses with the
  // assistant's own output rather than the room.
  model_speaking: { energy: 0.98, levelGain: 0.02, speed: 1.15, levelSpeed: 0.7 },
  interrupted: { energy: 0.7, levelGain: 0, speed: 0.6, levelSpeed: 0 },
  reconnecting: { energy: 0.8, levelGain: 0, speed: 1.9, levelSpeed: 0 },
  ended: { energy: 0.72, levelGain: 0, speed: 0.35, levelSpeed: 0 },
  // Failed: still and drained. Banked embers, gone out.
  error: { energy: 0.5, levelGain: 0, speed: 0, levelSpeed: 0 },
}

const FALLBACK_PALETTE: Palette = {
  arc: [28, 20, 16],
  glowStrength: 1.9,
}

/** Parses a colour the browser has already resolved to rgb() or rgba() form. */
function parseRgb(
  value: string,
  fallback: readonly [number, number, number],
): readonly [number, number, number] {
  const numbers = value.match(/-?\d+(\.\d+)?/g)
  if (!numbers || numbers.length < 3) return fallback
  return [Number(numbers[0]), Number(numbers[1]), Number(numbers[2])] as const
}

function readPalette(element: HTMLElement): Palette {
  if (typeof getComputedStyle !== 'function') return FALLBACK_PALETTE
  const style = getComputedStyle(element)
  const strength = Number.parseFloat(style.getPropertyValue('--blossom-glow-strength'))
  return {
    arc: parseRgb(style.color, FALLBACK_PALETTE.arc),
    glowStrength: Number.isFinite(strength) ? strength : FALLBACK_PALETTE.glowStrength,
  }
}

/** The mark's proportions: the arc spans 270 degrees, open at the lower right. */
const ARC_START = 0
const ARC_SWEEP = (Math.PI * 3) / 2
/** Where the dot rests in the logo: the arc's lower terminus. */
const DOT_HOME = Math.PI / 2

/**
 * How far into its own gap the arc reaches while Namu is thinking.
 *
 * Not the whole ninety degrees. A gap that closes completely is a ring, and
 * the mark would stop being the mark for as long as the answer took, which on
 * this model can be several seconds.
 */
const THINKING_CLOSE = (Math.PI / 2) * 0.78

/**
 * One close-and-open, in seconds.
 *
 * Sized against the wait rather than against the animation: a turn takes one
 * to three seconds, so this is slow enough that a typical wait shows a single
 * gesture. The state it replaced turned the arc a little under twice a second,
 * which at three seconds was five revolutions and read as a loading spinner
 * beside an interface whose whole argument is that the orb is the progress.
 *
 * Long waits do repeat it, and that is the point of a breath: it can be held
 * indefinitely without ever looking like a counter running down.
 */
const THINKING_PERIOD_S = 2.6

/**
 * Brightness is rate-limited, geometry is not.
 *
 * Anything that flashes more than three times a second is a seizure risk, and
 * a loud voice can swing a level from 0 to 1 and back several times a second.
 * Clamping how fast the *luminance* may travel keeps the orb safe without
 * making the shape sluggish, which is where the expressiveness actually lives.
 */
const MAX_GLOW_SLEW_PER_SECOND = 4

function rgba(color: readonly [number, number, number], alpha: number): string {
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${Math.max(0, Math.min(1, alpha))})`
}

/**
 * Cheap organic displacement.
 *
 * Three sine terms at incommensurable frequencies never repeat visibly, cost
 * a few multiplications per point, and need no noise library. A simplex
 * implementation would look marginally better and cost far more on the phones
 * this is actually for.
 */
function wobble(angle: number, time: number): number {
  return (
    0.6 * Math.sin(3 * angle + time * 0.7) +
    0.3 * Math.sin(5 * angle - time * 1.1) +
    0.2 * Math.sin(7 * angle + time * 0.53)
  )
}

/** Smoothly approaches a target with a time constant, framerate independent. */
function approach(current: number, target: number, dt: number, tau: number): number {
  if (tau <= 0) return target
  const alpha = Math.exp(-dt / tau)
  return alpha * current + (1 - alpha) * target
}

export class BlossomRenderer {
  private canvas: HTMLCanvasElement | null = null
  private context: CanvasRenderingContext2D | null = null
  private width = 0
  private height = 0
  private dpr = 1

  private frame: BlossomFrame = {
    state: 'idle',
    level: 0,
    muted: false,
    held: false,
    reducedMotion: false,
  }

  private palette: Palette = FALLBACK_PALETTE
  private lastAt = 0
  /** Seconds since the renderer started. Drives every periodic motion. */
  private clock = 0
  private smoothLevel = 0
  private glow = 0
  private fill = 0
  private dotAngle = DOT_HOME
  /** 0..1, how far the arc has reached into its gap. Thinking, as a shape. */
  private closing = 0
  /**
   * Seconds spent in the current thinking state, reset on leaving it.
   *
   * The breath is measured from when thinking began rather than from the
   * renderer's own clock. Driving it off `clock` meant the phase at entry was
   * whatever the clock happened to be on, so about half of all turns opened
   * with the arc already closed and then opening, which reads backwards.
   */
  private thinkingFor = 0

  attach(canvas: HTMLCanvasElement): void {
    this.canvas = canvas
    this.context = canvas.getContext('2d')
    this.refreshPalette()
    this.resize()
  }

  /**
   * Re-reads the colour tokens. Called on attach, on resize, and whenever the
   * colour scheme changes, which is the case a canvas cannot notice on its own
   * the way a CSS rule would.
   */
  refreshPalette(): void {
    if (this.canvas) this.palette = readPalette(this.canvas)
  }

  detach(): void {
    this.canvas = null
    this.context = null
  }

  /** Whether a 2D context was actually available. jsdom returns null. */
  get isReady(): boolean {
    return this.context !== null
  }

  update(frame: BlossomFrame): void {
    this.frame = frame
  }

  /**
   * Measures the element and sizes the backing store. Called on mount and on
   * resize only: reading layout inside the animation loop is the classic way
   * to turn a 60fps animation into a 20fps one.
   */
  resize(): void {
    const canvas = this.canvas
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    this.refreshPalette()
    // Capping at 2 keeps a 3x phone from rendering 2.25 times the pixels for a
    // difference nobody can see on a soft-edged glow.
    this.dpr = Math.min(2, typeof devicePixelRatio === 'number' ? devicePixelRatio : 1)
    this.width = Math.max(1, rect.width)
    this.height = Math.max(1, rect.height)
    canvas.width = Math.round(this.width * this.dpr)
    canvas.height = Math.round(this.height * this.dpr)
    this.context?.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
  }

  draw(now: number): void {
    const context = this.context
    if (!context) return

    const dt = this.lastAt === 0 ? 1 / 60 : Math.min(0.1, (now - this.lastAt) / 1000)
    this.lastAt = now
    this.clock += dt

    this.advance(dt)
    this.paint(context)
  }

  /** Moves every animated quantity one step toward where this state wants it. */
  private advance(dt: number): void {
    const { state, level, muted, held, reducedMotion } = this.frame

    // Under reduced motion nothing oscillates, so the level is held at a
    // steady value and the state reads through colour and weight instead.
    const targetLevel = reducedMotion ? (level > 0.05 ? 0.5 : 0) : level
    this.smoothLevel = approach(this.smoothLevel, targetLevel, dt, 0.09)

    const listening = state === 'listening' || state === 'user_speaking'
    const speaking = state === 'model_speaking'

    const glowTarget = held || muted ? 0 : listening ? 0.18 + this.smoothLevel * 0.82 : 0
    const fillTarget = held ? 0 : speaking ? 0.25 + this.smoothLevel * 0.75 : 0

    // Brightness travels at a bounded rate. See MAX_GLOW_SLEW_PER_SECOND.
    const maxStep = MAX_GLOW_SLEW_PER_SECOND * dt
    this.glow += Math.max(-maxStep, Math.min(maxStep, glowTarget - this.glow))
    this.fill += Math.max(-maxStep, Math.min(maxStep, fillTarget - this.fill))

    if (reducedMotion) {
      /*
       * Motion is how thinking is normally shown and it is exactly what is not
       * available here, so the shape carries it instead: the arc sits part way
       * into its own gap and stays there for as long as the answer takes. A
       * state held for three seconds has to be legible to somebody who has
       * asked for nothing to move, and colour alone puts it too close to idle.
       */
      this.thinkingFor = 0
      this.closing = state === 'processing' ? 0.55 : 0
      this.dotAngle = DOT_HOME - this.closing * THINKING_CLOSE
      return
    }

    if (state === 'processing') {
      this.thinkingFor += dt
      // Starts closed at zero and returns to zero, so the gesture begins from
      // the mark at rest however long ago the renderer started.
      const breath =
        (1 - Math.cos((this.thinkingFor / THINKING_PERIOD_S) * Math.PI * 2)) / 2
      this.closing = approach(this.closing, breath, dt, 0.1)
      // The dot rides the arc's live terminus rather than travelling on its
      // own. That is what separates this from a bead going round a track: the
      // mark is closing, and the dot is the end of it.
      this.dotAngle = DOT_HOME - this.closing * THINKING_CLOSE
      return
    }

    this.thinkingFor = 0
    /*
     * Eased rather than dropped, so an answer arriving mid-breath opens the
     * mark back up instead of snapping it open on the first frame of speech.
     * Quicker than the dot's own return, which leaves the dot trailing inside
     * the gap for a moment: that is the listening pose, and arriving in it is
     * the right way to leave a thought.
     */
    this.closing = approach(this.closing, 0, dt, 0.09)

    if (listening && !muted && !held) {
      // The dot leans into the open gap and sways, like someone waiting in a
      // doorway. Twenty degrees, not ninety: further than this and it reads as
      // a bead that has come loose from the mark rather than the mark itself
      // paying attention.
      const sway = Math.sin(this.clock * 1.6) * 0.5 + 0.5
      this.dotAngle = approach(this.dotAngle, DOT_HOME - sway * (Math.PI / 9), dt, 0.18)
    } else {
      this.dotAngle = approach(this.dotAngle, DOT_HOME, dt, 0.22)
    }
  }

  private paint(context: CanvasRenderingContext2D): void {
    const { state, muted, held, reducedMotion } = this.frame
    const cx = this.width / 2
    const cy = this.height / 2
    // The mark leaves generous clear space, which the brand rules ask for.
    const radius = Math.min(this.width, this.height) * 0.33

    context.clearRect(0, 0, this.width, this.height)

    const failed = state === 'error'
    const waiting = state === 'reconnecting'
    const dimmed = muted || held || failed

    // Breathing: about a quarter of a hertz, two percent of the radius. Small
    // enough to read as alive rather than as an animation.
    const breath = reducedMotion ? 0 : Math.sin(this.clock * Math.PI * 2 * 0.25) * 0.02
    const speakingSwell = state === 'model_speaking' ? this.smoothLevel * 0.06 : 0
    /*
     * Thinking draws inward, and that is the one gesture no other state makes:
     * listening pushes light out, speaking swells. Read off `closing` rather
     * than off the state, so the contraction and the arc ease together instead
     * of one of them jumping when the answer arrives.
     */
    const gather = this.closing * 0.055
    const baseRadius = radius * (1 + breath + speakingSwell - gather)

    // The voice deforms the ring only while the person is actually talking.
    const deform =
      !reducedMotion && state === 'user_speaking' ? 0.02 + this.smoothLevel * 0.1 : 0

    // The field first, so the arc is drawn over its edge and reads as the rim
    // holding it in.
    this.paintFlow(context, cx, cy, baseRadius)

    const strokeColor = failed ? KOLA : this.palette.arc
    const strokeAlpha = failed ? 0.55 : dimmed ? 0.3 : 0.86
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = rgba(strokeColor, strokeAlpha)
    context.lineWidth = Math.max(1.5, radius * (dimmed ? 0.035 : 0.046))

    if (muted || waiting) {
      // A dashed stroke says "not a continuous line" without any motion, which
      // is the one reading that survives both reduced motion and a glance.
      context.setLineDash([radius * 0.14, radius * 0.1])
    } else {
      context.setLineDash([])
    }

    this.paintArc(context, cx, cy, baseRadius, deform)

    context.setLineDash([])
    this.paintDot(context, cx, cy, baseRadius, radius)
  }

  /** Glow alpha, scaled for the ground it is being drawn on. */
  private glowAlpha(base: number): number {
    return base * this.palette.glowStrength
  }

  /**
   * The field inside the mark.
   *
   * Four soft colour masses drifting on Lissajous paths at incommensurable
   * speeds, clipped to the circle and feathered at the rim. Because the paths
   * never share a period they never repeat visibly, which is what separates
   * something that looks alive from something that looks like a loop.
   *
   * It replaces the earlier halo and inner fill, which said the same thing
   * twice and said it flatly. State is now carried entirely by how the field
   * behaves: how fast it moves, how far the masses travel from the centre, and
   * how much colour is in them. That matters more than it used to, because the
   * visible status text is gone and this is the only thing left saying what is
   * happening.
   *
   * Four gradients per frame is well within budget on a low-end phone: no
   * blur filter, no per-pixel work, no allocation beyond the gradient objects
   * the canvas needs anyway.
   */
  private paintFlow(
    context: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
  ): void {
    const { state, muted, held, reducedMotion } = this.frame
    const behaviour = FLOW_BEHAVIOUR[state]
    const stilled = muted || held
    const level = this.smoothLevel

    const energy = stilled ? 0.55 : behaviour.energy + level * behaviour.levelGain
    const speed = stilled ? 0.3 : behaviour.speed + level * behaviour.levelSpeed
    if (energy <= 0.01) return

    // The clock the field moves on. Reduced motion pins it, so the masses sit
    // where they are and only their colour carries the state.
    const time = reducedMotion ? 8.4 : this.clock

    const onInk = this.isDarkGround()
    const box = radius * 2

    context.save()
    context.beginPath()
    context.arc(cx, cy, radius * 0.98, 0, Math.PI * 2)
    context.clip()

    /**
     * One saturated pole fading to light, then wisps over the top.
     *
     * An earlier version layered five translucent colour masses and averaged
     * them together, which is exactly how to make mud: every added layer pulls
     * the result toward the mean, and the mean of warm colours on a warm page
     * is beige. The structure that reads as luminous is the opposite. A single
     * strong gradient carries the colour, and lighter masses on top carry the
     * movement.
     *
     * The pole drifts, so the whole field turns slowly rather than sitting
     * still with texture crawling across it.
     */
    // A linear gradient, not a radial one. A radial base puts the saturated
    // colour in a small core and leaves most of the disc pale, which on a warm
    // page is indistinguishable from the page. Sweeping the colour across the
    // whole circle is what gives one half real saturation and the other half
    // real light, and it is the structure the reference actually has.
    // The axis turns slowly, so the field rolls rather than sits.
    const axis = time * 0.11 * speed
    const gx = Math.cos(axis)
    const gy = Math.sin(axis)
    const base = context.createLinearGradient(
      cx - gx * radius,
      cy - gy * radius,
      cx + gx * radius,
      cy + gy * radius,
    )

    if (onInk) {
      // On Ink there is nothing underneath, so the field is the light source.
      base.addColorStop(0, rgba(SAHEL, 0.96 * energy))
      base.addColorStop(0.52, rgba(SAHEL, 0.58 * energy))
      base.addColorStop(0.84, rgba(KOLA, 0.42 * energy))
      base.addColorStop(1, rgba(KOLA, 0.16 * energy))
    } else {
      // On cream the disc has to be clearly coloured at one end and brighter
      // than the page at the other, or it disappears into the ground.
      base.addColorStop(0, rgba(SAHEL, 0.98 * energy))
      base.addColorStop(0.4, rgba(SAHEL, 0.72 * energy))
      base.addColorStop(0.72, rgba(CLAY, 0.85 * energy))
      base.addColorStop(1, rgba(PAPER, 0.98 * energy))
    }
    context.fillStyle = base
    context.fillRect(cx - radius, cy - radius, box, box)

    /**
     * The wisps. Pale, tight, and moving on their own paths, which is what
     * turns a gradient into weather.
     */
    const wispColour = onInk ? HARMATTAN : PAPER
    for (const wisp of WISPS) {
      // The field gathers with the arc. While Namu is thinking the masses draw
      // off the rim toward the centre, so the light concentrates rather than
      // simply dimming, which is the difference between concentrating and
      // going out.
      const travel = (0.7 + energy * 0.6) * (1 - this.closing * 0.42)
      const wx = cx + Math.sin(time * wisp.wx * speed + wisp.phase) * radius * wisp.ax * travel
      const wy = cy + Math.cos(time * wisp.wy * speed + wisp.phase * 1.31) * radius * wisp.ay * travel
      const size = radius * wisp.size * (0.9 + energy * 0.25)

      const gradient = context.createRadialGradient(wx, wy, 0, wx, wy, size)
      const peak = wisp.alpha * energy
      gradient.addColorStop(0, rgba(wispColour, peak))
      gradient.addColorStop(0.45, rgba(wispColour, peak * 0.45))
      gradient.addColorStop(1, rgba(wispColour, 0))
      context.fillStyle = gradient
      context.fillRect(cx - radius, cy - radius, box, box)
    }

    // Feather the rim by erasing alpha outward, so the field dissolves into the
    // ground instead of stopping at a hard clipped edge. Done with the
    // composite operation rather than a colour, so it works on either ground.
    const feather = context.createRadialGradient(cx, cy, radius * 0.72, cx, cy, radius)
    feather.addColorStop(0, 'rgba(0, 0, 0, 0)')
    feather.addColorStop(1, 'rgba(0, 0, 0, 1)')
    context.globalCompositeOperation = 'destination-out'
    context.fillStyle = feather
    context.fillRect(cx - radius, cy - radius, box, box)

    context.restore()
  }

  /** True when the arc is pale, which means it is being drawn on Ink. */
  private isDarkGround(): boolean {
    const [r, g, b] = this.palette.arc
    return (r * 0.299 + g * 0.587 + b * 0.114) / 255 > 0.5
  }

  private paintArc(
    context: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    deform: number,
  ): void {
    /*
     * Never less than the full 270 degrees, and only ever more.
     *
     * An earlier version animated the arc drawing itself in on the first
     * frames. It looked good and it was wrong: any moment the render loop is
     * not running, the mark is a fragment. A backgrounded tab throttles
     * requestAnimationFrame to nothing, and the frozen frame is whatever was
     * half-drawn. The entrance now lives in CSS, on the compositor, where it
     * cannot leave the brand mark broken.
     *
     * Thinking extends the sweep into the gap, which is safe under the same
     * argument in a way that shrinking it would not be: a frozen frame of this
     * is a slightly more closed mark, never a broken one.
     */
    const sweep = ARC_SWEEP + this.closing * THINKING_CLOSE
    context.beginPath()

    if (deform === 0) {
      // A true arc when there is nothing to deform: cheaper, and perfectly
      // circular in a way a polyline never quite is.
      context.arc(cx, cy, radius, ARC_START, ARC_START - sweep, true)
      context.stroke()
      return
    }

    // Enough points that the outline reads as smooth at any orb size, few
    // enough that the whole path costs well under a millisecond.
    const steps = 96
    for (let i = 0; i <= steps; i += 1) {
      const angle = ARC_START - (sweep * i) / steps
      const r = radius * (1 + (deform * wobble(angle, this.clock)) / 1.1)
      const x = cx + Math.cos(angle) * r
      const y = cy + Math.sin(angle) * r
      if (i === 0) context.moveTo(x, y)
      else context.lineTo(x, y)
    }
    context.stroke()
  }

  private paintDot(
    context: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    baseRadius: number,
  ): void {
    const { state, muted, held } = this.frame
    const failed = state === 'error'
    const dimmed = muted || held || failed

    // The voice pushes the dot outward, which is the most direct way to show
    // someone their own speech moving something.
    const push = state === 'user_speaking' ? this.smoothLevel * baseRadius * 0.1 : 0
    const x = cx + Math.cos(this.dotAngle) * (radius + push)
    const y = cy + Math.sin(this.dotAngle) * (radius + push)
    const size = baseRadius * (dimmed ? 0.07 : 0.082 + this.smoothLevel * 0.022)

    if (!dimmed && this.glow > 0.02) {
      const halo = context.createRadialGradient(x, y, 0, x, y, size * 4)
      halo.addColorStop(0, rgba(SAHEL, this.glowAlpha(0.35) * this.glow))
      halo.addColorStop(1, rgba(SAHEL, 0))
      context.fillStyle = halo
      context.beginPath()
      context.arc(x, y, size * 4, 0, Math.PI * 2)
      context.fill()
    }

    context.fillStyle = dimmed ? rgba(KOLA, failed ? 0.9 : 0.75) : rgba(SAHEL, 1)
    context.beginPath()
    context.arc(x, y, size, 0, Math.PI * 2)
    context.fill()
  }
}
